/**
 * Hero Carousel Section JavaScript
 * Swiper-based carousel with infinite loop and autoplay
 * @version 2.2.0
 */

(function() {
  'use strict';

  // Cache mobile breakpoint check
  let isMobileCache = null;
  let lastWidth = 0;

  function checkMobile() {
    const currentWidth = window.innerWidth;
    if (currentWidth !== lastWidth) {
      lastWidth = currentWidth;
      isMobileCache = currentWidth < 990;
    }
    return isMobileCache;
  }

  class HeroCarousel {
    constructor(container) {
      if (!container) return;

      this.container = container;
      this.swiper = null;
      this.isDestroyed = false;

      // Cache all DOM queries upfront
      this.cacheDOM();
      this.config = this.parseConfig();

      this.init();
    }

    cacheDOM() {
      this.swiperEl = this.container.querySelector('.hero-carousel__swiper');
      this.slides = this.container.querySelectorAll('.hero-carousel__slide');
      this.prevArrow = this.container.querySelector('.hero-carousel__arrow--prev');
      this.nextArrow = this.container.querySelector('.hero-carousel__arrow--next');
      this.paginationEl = this.container.querySelector('.hero-carousel__pagination');
    }

    parseConfig() {
      const d = this.container.dataset;
      return {
        autoplay: d.autoplay === 'true',
        autoplaySpeed: parseInt(d.autoplaySpeed, 10) || 5000,
        transitionSpeed: parseInt(d.transitionSpeed, 10) || 500,
        pauseOnHover: d.pauseOnHover === 'true'
      };
    }

    init() {
      if (typeof Swiper === 'undefined' || !this.swiperEl) return;

      const slideCount = this.slides.length;
      if (slideCount <= 1) {
        this.container.classList.add('hero-carousel--single-slide', 'hero-carousel--initialized');
        return;
      }

      const self = this;
      const isMobile = checkMobile();
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.swiper = new Swiper(this.swiperEl, {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: true,
        speed: reducedMotion ? 1 : this.config.transitionSpeed,
        loopedSlides: slideCount,
        loopPreventsSliding: false,

        autoplay: this.config.autoplay ? {
          delay: this.config.autoplaySpeed,
          disableOnInteraction: false,
          pauseOnMouseEnter: this.config.pauseOnHover,
          stopOnLastSlide: false,
          waitForTransition: true
        } : false,

        navigation: {
          nextEl: this.nextArrow,
          prevEl: this.prevArrow
        },

        pagination: {
          el: this.paginationEl,
          clickable: true
        },

        allowTouchMove: isMobile,
        grabCursor: isMobile,
        keyboard: { enabled: false },

        a11y: {
          prevSlideMessage: 'Previous slide',
          nextSlideMessage: 'Next slide',
          paginationBulletMessage: 'Go to slide {{index}}'
        },

        on: {
          init: function() {
            self.container.classList.add('hero-carousel--initialized');
          }
        }
      });

      this.setupEventListeners();
    }

    setupEventListeners() {
      let resizeTimeout;
      this.resizeHandler = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          if (this.swiper && !this.isDestroyed) {
            const mobile = checkMobile();
            this.swiper.allowTouchMove = mobile;
            this.swiper.params.grabCursor = mobile;
            this.swiper.update();
          }
        }, 200);
      };
      window.addEventListener('resize', this.resizeHandler);

      this.visibilityHandler = () => {
        if (!this.swiper?.autoplay || !this.config.autoplay) return;
        document.hidden ? this.swiper.autoplay.stop() : this.swiper.autoplay.start();
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          // Single element observed, access directly instead of forEach
          const entry = entries[0];
          if (!this.swiper?.autoplay || !this.config.autoplay) return;
          entry.isIntersecting ? this.swiper.autoplay.start() : this.swiper.autoplay.stop();
        }, { threshold: 0.3 });
        this.observer.observe(this.container);
      }
    }

    destroy() {
      this.isDestroyed = true;
      if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
      if (this.visibilityHandler) document.removeEventListener('visibilitychange', this.visibilityHandler);
      if (this.observer) this.observer.disconnect();
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      this.container.classList.remove('hero-carousel--initialized');
    }

    goToSlide(index) {
      this.swiper?.slideToLoop(index);
    }
  }

  class HeroCarouselManager {
    constructor() {
      this.instances = new Map();
      this.init();
    }

    init() {
      document.querySelectorAll('.hero-carousel-section').forEach((el) => {
        this.createInstance(el);
      });
      this.setupShopifyEvents();
    }

    createInstance(container) {
      const id = container.dataset.sectionId || container.id;
      if (this.instances.has(id)) this.instances.get(id).destroy();
      const instance = new HeroCarousel(container);
      this.instances.set(id, instance);
      return instance;
    }

    destroyInstance(id) {
      if (this.instances.has(id)) {
        this.instances.get(id).destroy();
        this.instances.delete(id);
      }
    }

    setupShopifyEvents() {
      if (typeof Shopify === 'undefined' || !Shopify.designMode) return;

      document.addEventListener('shopify:section:load', (e) => {
        const el = e.target.querySelector('.hero-carousel-section');
        if (el) this.createInstance(el);
      });

      document.addEventListener('shopify:section:unload', (e) => {
        const el = e.target.querySelector('.hero-carousel-section');
        if (el) this.destroyInstance(el.dataset.sectionId || el.id);
      });

      document.addEventListener('shopify:section:select', (e) => {
        const el = e.target.querySelector('.hero-carousel-section');
        if (el) this.instances.get(el.dataset.sectionId)?.swiper?.autoplay?.stop();
      });

      document.addEventListener('shopify:section:deselect', (e) => {
        const el = e.target.querySelector('.hero-carousel-section');
        const instance = el && this.instances.get(el.dataset.sectionId);
        if (instance?.swiper?.autoplay && instance.config.autoplay) {
          instance.swiper.autoplay.start();
        }
      });

      document.addEventListener('shopify:block:select', (e) => {
        const section = e.target.closest('.hero-carousel-section');
        if (!section) return;
        const instance = this.instances.get(section.dataset.sectionId);
        if (instance?.swiper) {
          // Use cached slides from instance
          const slides = instance.slides;
          for (let i = 0; i < slides.length; i++) {
            if (slides[i].dataset.blockId === e.detail.blockId) {
              instance.goToSlide(i);
              break;
            }
          }
          instance.swiper.autoplay?.stop();
        }
      });
    }

    getInstance(id) {
      return this.instances.get(id) || null;
    }
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.heroCarouselManager = new HeroCarouselManager();
    });
  } else {
    window.heroCarouselManager = new HeroCarouselManager();
  }

  window.HeroCarousel = HeroCarousel;
  window.HeroCarouselManager = HeroCarouselManager;
})();
